import React, { useContext, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Typography, Form, Input, InputNumber, Space, Radio, Button, notification, Select, Row, Col, Alert, Modal } from 'antd'
import { LoadingOutlined, ExclamationCircleOutlined, MinusCircleOutlined, PlusCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { API, graphqlOperation } from 'aws-amplify'
import { useIntl } from 'react-intl'
import { notificationStore } from '../../context/notificationsContext/store'
import { getAutomationsById } from '../../graphql/custom-queries'

import IntlMessages from '../../util/IntlMessages'
import CircularProgress from '../circular-progress'

import { IAutomationSeniorityEntitlementForm } from '@vacationtracker/shared/types/automations'
import { IGetLocationsShort } from '../../types/locations'
import { IGetAutomationsByIdData, ILeaveTypeListShort, ISenitorityAutomation } from '../../types/custom-queries'
import { IData } from '../../types/data'
import { openSupportChat } from '../../util/open-support-chat'
import { getDaysOrHours, setDaysOrHours } from '@vacationtracker/shared/functions/get-days-or-hours'
import uniqBy from 'lodash/uniqBy'


const { confirm } = Modal
const { Text } = Typography
const { Option } = Select

interface ISeniorityEntitlementForm {
  locations: IGetLocationsShort[]
  optionLocations: IGetLocationsShort[]
  leaveTypes: ILeaveTypeListShort[]
  backToAutomations: () => void
  automationId?: string
  hourlyLeaveAccounting: boolean
}

const SeniorityEntitlementForm = ({
  locations,
  optionLocations,
  leaveTypes,
  backToAutomations,
  automationId,
  hourlyLeaveAccounting,
}: ISeniorityEntitlementForm) => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [applyToAllUsers, setApplyToAllUsers] = useState(optionLocations.length === locations.length ? true : false)
  const [isLoading, setIsLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)
  const [showPeriodError, setShowPeriodError] = useState(false)
  const [showWarningStartDate, setShowWarningStartDate] = useState(false)
  const [automation, setAutomation] = useState<IAutomationSeniorityEntitlementForm>({
    automationId: '',
    name: '',
    leaveTypeId: '',
    calculateFrom: 'TODAY',
    awardSeniorityEntitlementDaysOn: 'USER_WORK_ANNIVERSARY',
    applyToAllUsers: optionLocations.length === locations.length ? true : false,
    isActive: true,
    prorate: false,
    locations: [],
    teams: [],
    labels: [],
    periods: [
      {'days': hourlyLeaveAccounting ? 16 : 2, 'years': 5},
    ],
  })
  const [automationPeriods, setAutomationPeriods] = useState<IAutomationSeniorityEntitlementForm['periods']>([
    {'days': hourlyLeaveAccounting ? 16 : 2, 'years': 5},
  ])
  const [startSeniorityEntitlement] = useState([
    { value: 'TODAY', name: formatMessage({ id: 'app.today'}) },
    { value: 'USER_EMPLOYMENT_DATE', name: formatMessage({ id: 'components.locationForm.userYear'})},
  ])
  const [awardSeniorityEntitlementDaysOn] = useState([
    { value: 'USER_WORK_ANNIVERSARY', name: formatMessage({ id: 'automationsForm.seniorityEntitlement.employeeStartDate'}) },
    { value: 'BEGINNING_OF_THE_FISCAL_YEAR', name: formatMessage({ id: 'automationsForm.seniorityEntitlement.beginningOfTheFiscalYear'})},
  ])
  const [disableProrate, setDisableProrate] = useState(false)
  const [disableAwardSeniorityEntitlement, setDisableAwardSeniorityEntitlement] = useState(false)

  useEffect(() => {
    if (automationId) {
      fetchAutomations(automationId)
    }
  }, [automationId])

  const fetchAutomations = async (id) => {
    try {
      const response = await API.graphql(graphqlOperation(getAutomationsById, { id })) as IData<IGetAutomationsByIdData>
      const getAutomationData = response.data.getAutomation as ISenitorityAutomation

      const applyToAllUsers = getAutomationData?.locations.length === locations.length
      form.setFieldsValue({ applyToAllUsers })
      form.setFieldsValue({ leaveTypeId: getAutomationData.leaveTypeId })
      setApplyToAllUsers(applyToAllUsers)

      const periods = getAutomationData.periods.map(period => {
        return {
          ...period,
          days: getDaysOrHours(period.days, hourlyLeaveAccounting),
        }
      })
      setAutomation({
        automationId: getAutomationData.id,
        name: getAutomationData.name,
        leaveTypeId: getAutomationData.leaveTypeId,
        calculateFrom: getAutomationData?.calculateFrom || 'TODAY',
        applyToAllUsers: applyToAllUsers,
        isActive: getAutomationData.isActive,
        locations: getAutomationData.locations,
        teams: getAutomationData.teams,
        labels: getAutomationData.labels,
        periods,
        prorate: getAutomationData.prorate,
      } as IAutomationSeniorityEntitlementForm)

      setAutomationPeriods(periods)

      setIsLoading(false)
    } catch (err) {
      console.log('ERROR FETCH DATA', err)
    }
  }

  const showConfirm = () => {
    if(showWarningStartDate) {
      confirm({
        title: formatMessage({ id: 'components.seniorityEntitlement.confirmTitle' }),
        icon: <ExclamationCircleOutlined />,
        content: formatMessage({ id: 'components.seniorityEntitlement.confirmDescription' }),
        width: 600,
        onOk() {
          onFinish()
        },
      })
    } else {
      onFinish()
    }
  }

  const onFinish = () => {
    form
      .validateFields()
      .then(async (values) => {
        setBtnLoading(true)
        let response
        try {
          const data = {
            name: values.name,
            leaveTypeId: values.leaveTypeId,
            calculateFrom: values.calculateFrom,
            periods: values.periods,
            locations: values.locations ? [values.locations] : locations.map(location => location.id),
            prorate: values.prorate,
          }
          if(hourlyLeaveAccounting) {
            data.periods = data.periods.map(period => {
              return {
                ...period,
                days: setDaysOrHours(period.days, hourlyLeaveAccounting),
              }
            })
          }

          const isExistSameYear = uniqBy(data.periods, 'years').length !== data.periods.length
          if (isExistSameYear) {
            throw new Error('components.seniorityEntitlement.sameYearError')
          }

          const isSortedDataPeriodsByYear = data.periods.every((period, index) => {
            if(index === 0) return true
            return period.years > data.periods[index - 1].years
          })
          if (!isSortedDataPeriodsByYear) {
            throw new Error('components.seniorityEntitlement.notSortedYearInPeriodError')
          }

          if(!automationId) {
            response = await API.post('CoreEvent', '/core/event', {
              body: {
                eventType: 'SENIORITY_ENTITLEMENT_CREATED',
                eventGroup: 'AUTOMATION',
                ...data,
              },
            })
          } else {
            response = await API.post('CoreEvent', '/core/event', {
              body: {
                eventType: 'SENIORITY_ENTITLEMENT_UPDATED',
                eventGroup: 'AUTOMATION',
                automationId,
                ...data,
                isActive: true,
              },
            })
          }
          
          notification.open({
            key: response.correlationId,
            message: formatMessage({ id: automationId ? 'automations.seniorityEntitlement.updateInProgress' : 'automations.seniorityEntitlement.createInProgress' }),
            icon: (<LoadingOutlined />),
            duration: 0,
          })
          setActionNotifications([ ...actionNotifications, response.correlationId ])

          history.push('/app/automations')
        } catch (error) {
          setBtnLoading(false)
          if (error.response?.data?.message) {
            notification.error({
              message: formatMessage({ id: 'error.generic' }),
              description: formatMessage({ id: error.response.data.message }),
              duration: 0,
            })
          } else if (typeof error?.message === 'string') {
            notification.error({
              message: formatMessage({ id: 'error.generic' }),
              description: formatMessage({ id: error.message }),
              duration: 0,
            })
          } else {
            const description = response?.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)

            notification.error({
              message: formatMessage({ id: 'error.generic' }),
              description,
              duration: 0,
            })
          }
        }
      })
      .catch(info => {
        console.log('Validate Failed:', info)
        setBtnLoading(false)
        setShowPeriodError(true)
      })
  }
  
  const handleApplyToAllUsers = () => {
    setApplyToAllUsers(!applyToAllUsers)
  }

  const layout = {
    labelCol: { xs: { span: 24 }, sm: { span: 24 }, md: { span: 11 }, lg: { span: 11 }, xl: { span: 7 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 24 }, md: { span: 13 }, lg: { span: 13 }, xl: { span: 12 } },
  }
  
  const periods = {
    labelCol: { xs: { span: 0 }, sm: { span: 0 }, md: { span: 3 }, lg: { span: 4 }, xl: { span: 5 } },
    wrapperCol: { xs: { span: 24 }, sm: { span: 24 }, md: { span: 21 }, lg: { span: 20 }, xl: { span: 19 } },
  }

  const tailLayout = {
    wrapperCol: { xs: { span: 24, offset: 0 }, sm: { span: 24, offset: 0 }, md: { span: 12, offset: 7 }, lg: { span: 12, offset: 7 } },
  }

  const validatePeriods = (periods) => {
    if(periods[periods.length - 1].days === undefined || periods[periods.length - 1].years === undefined) {
      setShowPeriodError(true)
    }
  }

  const onChangePeriodYears = (value, index) => {
    const newAutomationPeriods = automationPeriods.map((period, i) => 
      i === index ? { ...period, years: value } : period
    )
    setAutomationPeriods(newAutomationPeriods)
  }
  
  const onChangePeriodDays = (value, index) => {
    const newAutomationPeriods = automationPeriods.map((period, i) => 
      i === index ? { ...period, days: value } : period
    )
    setAutomationPeriods(newAutomationPeriods)
  }

  const onChangeStartDate = (value) => {
    if (value === 'USER_EMPLOYMENT_DATE') {
      setShowWarningStartDate(true)
    } else {
      setShowWarningStartDate(false)
    }
  }

  const onChangeLocation = (locationId) => {
    const location = locations.find(location => location.id === locationId)
    if (location?.resetQuotas === 'USER_YEAR') {
      form.setFieldsValue({ awardSeniorityEntitlementDaysOn: 'USER_WORK_ANNIVERSARY' })
      setDisableAwardSeniorityEntitlement(true)
      setDisableProrate(true)
      form.setFieldsValue({ prorate: false })
    } else {
      setDisableAwardSeniorityEntitlement(false)
      setDisableProrate(false)
    }

  }

  const onChangeAwardDaysOn = (value) => {
    if (value === 'BEGINNING_OF_THE_FISCAL_YEAR') {
      setDisableProrate(true)
      form.setFieldsValue({ prorate: false })
    } else {
      setDisableProrate(false)
    }
  }

  const openCrisp = (e) => {
    e.preventDefault()
    openSupportChat()
  }

  let minNumber = 1
  const currentPeriods = form.getFieldValue('periods')
  if (currentPeriods?.length > 1) {
    minNumber = currentPeriods[currentPeriods.length - 1].years as number
  } else {
    minNumber = 1
  }
  
  return (
    <>
      {optionLocations.length === 0 && 
        <Alert
          message={<IntlMessages id="components.seniorityEntitlement.noLocations"/>}
          type="warning"
          style={{ marginBottom: 20 }}
        />
      }
      {(isLoading && automationId) ? 
        <CircularProgress /> :
        <>
          <div style={{ marginBottom: 20 }}>
            <Text> <IntlMessages id="choiceAutomations.seniorityEntitlementInfo" values={{ link: (...chunks) => <a onClick={openCrisp}>{chunks}</a> }}/></Text>
          </div>
          <Form
            layout="horizontal"
            form={form}
            name="automationsForm"
            initialValues={automation}
            onFinish={showConfirm}
            scrollToFirstError
            disabled={Boolean(automationId) || optionLocations.length === 0}
            className="automation-seniority-entitlement-form"
            onFinishFailed={(error) => {
              validatePeriods(error.values.periods)
            }}
            {...layout}
          >
            <Form.Item
              label={<IntlMessages id="app.name" />}
              name="name"
              rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
            >
              <Input style={{ maxWidth: 300 }}/>
            </Form.Item>

            <Form.Item
              label={<IntlMessages id="automationsForm.applyToAllUsers" />}
              tooltip={<IntlMessages id="automationsForm.seniorityEntitlement.applyToAllUsersTooltip" />}
              name="applyToAllUsers"
            >
              <Radio.Group onChange={handleApplyToAllUsers} disabled={optionLocations.length === locations.length ? false : true}>
                <Radio.Button value={true}><IntlMessages id="app.yes" /></Radio.Button>
                <Radio.Button value={false}><IntlMessages id="app.no" /></Radio.Button>
              </Radio.Group>
            </Form.Item>

            {!applyToAllUsers &&
              <Form.Item 
                label={<IntlMessages id="app.location" />}
                rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                name="locations"
              >
                <Select
                  style={{ width: '100%' }}
                  placeholder={<IntlMessages id="app.pleaseSelect" />}
                  onChange={onChangeLocation}
                >
                  {(automationId ? locations : optionLocations).map((location) => (
                    <Option value={location.id} key={location.id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            }

            <Form.Item 
              label={<IntlMessages id="app.leaveType" />}
              rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
              name="leaveTypeId"
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder={<IntlMessages id="app.pleaseSelect" />}
              >
                {leaveTypes.map((leaveType) => (
                  <Option value={leaveType.id} key={leaveType.id}>
                    {leaveType.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item 
              label={<IntlMessages id="components.seniorityEntitlement.startApplyingSeniorityEntitlementFrom" />}
              rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
              name="calculateFrom"
              extra={showWarningStartDate ?
                <span style={{ color: 'red', whiteSpace: 'break-spaces'}}>
                  <WarningOutlined /> <IntlMessages id="components.seniorityEntitlement.startApplyingSeniorityEntitlementFromInfo" />
                </span> : ''
              }
            >
              <Select
                allowClear
                onChange={onChangeStartDate}
                style={{ width: '100%' }}
                placeholder={<IntlMessages id="app.pleaseSelect" />}
              >
                {startSeniorityEntitlement.map((item) => (
                  <Option value={item.value} key={item.name}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item 
              label={<IntlMessages id="components.seniorityEntitlement.addDaysFrom" />}
              rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
              name="awardSeniorityEntitlementDaysOn"
            >
              <Select
                allowClear
                onChange={onChangeAwardDaysOn}
                style={{ width: '100%' }}
                placeholder={<IntlMessages id="app.pleaseSelect" />}
                disabled={disableAwardSeniorityEntitlement}
              >
                {awardSeniorityEntitlementDaysOn.map((item) => (
                  <Option value={item.value} key={item.name}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={<IntlMessages id="components.seniorityEntitlement.prorateForTheFirstYear" />}
              tooltip={<IntlMessages id="components.seniorityEntitlement.prorateForTheFirstYearTooltip" />}
              name="prorate"
            >
              <Radio.Group disabled={disableProrate}>
                <Radio.Button value={true}><IntlMessages id="app.yes" /></Radio.Button>
                <Radio.Button value={false}><IntlMessages id="app.no" /></Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Row className="ant-form-item">
              <Col {...periods.labelCol}></Col>
              <Col {...periods.wrapperCol}>
                <Form.List name="periods">
                  {(fields, { add, remove }) => {
                    return (
                      <>
                        {fields.map((field, index) => {
                          return (
                            <Space key={field.key} style={{ marginBottom: 15, display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                              <IntlMessages id="app.add" />
                              <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, curValues) =>
                                  prevValues.area !== curValues.area || prevValues.sights !== curValues.sights
                                }
                              >
                                {() => (
                                  <Form.Item
                                    noStyle
                                    name={[field.name, 'days']}
                                    rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                                  >
                                    <InputNumber 
                                      min={1 as number} 
                                      max={hourlyLeaveAccounting ? 2920 : 365 as number} 
                                      width={70}
                                      step={1}
                                      parser={value => Math.round(Number(value)) as number}
                                      onChange={(value) => onChangePeriodDays(value, index)}
                                    />
                                  </Form.Item>
                                )}
                              </Form.Item>
                              {hourlyLeaveAccounting ? 
                                <IntlMessages id="components.seniorityEntitlement.hoursAfter" /> : 
                                <IntlMessages id="components.seniorityEntitlement.daysAfter" />
                              }
                              <Form.Item
                                noStyle
                                name={[field.name, 'years']}
                                rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                              >
                                <InputNumber 
                                  defaultValue={minNumber}
                                  min={minNumber}
                                  max={100}
                                  width={70}
                                  step={1}
                                  parser={value => Math.round(Number(value) || minNumber)}
                                  onChange={(value) => onChangePeriodYears(value, index)}
                                  disabled={fields.length - 1 !== index}
                                />
                              </Form.Item>
                              <IntlMessages id="components.seniorityEntitlement.yearsInTheCOmpany" />

                              {fields.length > 1 && 
                                <MinusCircleOutlined 
                                  onClick={() => {
                                    remove(field.name)
                                    const periods = form.getFieldValue('periods')
                                    setAutomationPeriods(periods)
                                    const lastPeriod = periods[periods.length - 2]
                                    minNumber = lastPeriod?.years ? lastPeriod.years as number + 1 : 1
                                    setShowPeriodError(false)
                                  }}
                                  style={{ color: 'red' }}
                                />
                              }
                              {(fields.length - 1 === index && fields.length < 20) &&
                                <PlusCircleOutlined
                                  disabled={true}
                                  onClick={() => {
                                    const periods = form.getFieldValue('periods')
                                    const lastPeriod = periods[periods.length - 1]
                                    if (!lastPeriod || lastPeriod.days === undefined || lastPeriod.years === undefined) {
                                      setShowPeriodError(true)
                                      return 
                                    }
                                    minNumber = lastPeriod.years as number + 1
                                    const newPeriods = [...periods, {years: minNumber, days: 1}]
                                    setAutomationPeriods(newPeriods)
                                    form.setFieldsValue({ periods: newPeriods })
                                    setShowPeriodError(false)
                                  }} 
                                  style={{ color: 'green'}}
                                />
                              }
                            </Space>
                          )
                        })}
                      </>
                    )
                  }}
                </Form.List>
                {showPeriodError &&
                  <Text type="danger"><br/><IntlMessages id="components.seniorityEntitlement.allFiledsAreRequired" /></Text>
                }
              </Col>
            </Row>
            <Row>
              <Col xs="24">
                <Alert
                  style={{ marginBottom: 20 }}
                  message={
                    <>
                      <IntlMessages key="prefix" id="components.seniorityEntitlement.summaryInfoPrefix" />
                      {automationPeriods[0] &&
                        <IntlMessages 
                          key="fistRow"
                          id="components.seniorityEntitlement.summeryInfoFirstRow" 
                          values={{
                            days: automationPeriods[0].days,
                            year: automationPeriods[0].years,
                            showHour: (...chunks) => hourlyLeaveAccounting ? <>{chunks}</> : '',
                            showDays: (...chunks) => !hourlyLeaveAccounting ? <>{chunks}</> : '',
                          }}
                        />
                      }
                      {automationPeriods.length > 1 && automationPeriods.map((period, index) => {
                        if (index === 0) return (<span key="span"></span>)
                        return (
                          <IntlMessages
                            key={`other-${index}`}
                            id="components.seniorityEntitlement.summeryInfoMoreDays" 
                            values={{
                              days: period.days,
                              year: period.years,
                              showHour: (...chunks) => hourlyLeaveAccounting ? <>{chunks}</> : '',
                              showDays: (...chunks) => !hourlyLeaveAccounting ? <>{chunks}</> : '',
                            }}
                          />
                        )
                      })}
                      <IntlMessages 
                        key={'suffix'}
                        id="components.seniorityEntitlement.summaryInfoSuffix" 
                        values={{
                          days: automationPeriods.reduce((acc, period) => acc + period.days, 0),
                          showHour: (...chunks) => hourlyLeaveAccounting ? <>{chunks}</> : '',
                          showDays: (...chunks) => !hourlyLeaveAccounting ? <>{chunks}</> : '',
                        }}
                      />
                    </>
                  }
                />
              </Col>
            </Row>
            <Form.Item {...tailLayout}>
              <div>
                <Button type="default" style={{ marginRight: 10 }} onClick={backToAutomations}>
                  <IntlMessages id="app.cancel" />
                </Button>
                <Button type="primary" 
                  loading={btnLoading}
                  htmlType="submit"
                >
                  {automationId ? <IntlMessages id="app.update" /> : <IntlMessages id="app.create" />}
                </Button>
              </div>
            </Form.Item>
          </Form>
          {Boolean(automationId) &&
            <Alert
              message={<IntlMessages id="components.seniorityEntitlement.disableEditInfo" values={{ link: (...chunks) => <a onClick={openCrisp}>{chunks}</a> }}/>}
              type="warning"
            />
          }
        </>
      }
    </>
  )
}
export default SeniorityEntitlementForm
